/**
 * Repuso Reviews Synchronization Module
 * Compliant with Shopify CRO Development Guidelines (AGENTS.md)
 * 
 * Responsibility:
 * - Fetches verified ratings & review totals from the Repuso API.
 * - Caches data in localStorage to ensure zero layout shift and fast performance.
 * - Synchronizes review count and rating score to DOM elements marked with data-* attributes.
 */

(function () {
  'use strict';

  // Constants
  const API_ENDPOINT = 'https://api.repuso.com/public/v1/reports/ratings';
  const API_TOKEN = '2795eabf188ecdc4c6394b91368aca95c5ffffd6';
  const STORAGE_CACHE_KEY = 'repuso_reviews_data_cache';
  const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour TTL
  const DEFAULT_RATING_SCORE = 4.7;
  const DEFAULT_REVIEW_COUNT = 14425;
  // Fixed fallback for the "X+ reviews. Unfiltered. Since 2012" homepage testimonial subheading
  // (.main_subtite). Shown whenever the Repuso API doesn't return usable data, regardless of cache.
  const UNFILTERED_REVIEW_COUNT_FALLBACK = 30000;

  /* ==========================================================================
     Pure Business Logic Functions (State & API)
     ========================================================================== */

  /**
   * Formats numeric review count with locale separators.
   * @param {number|string} count 
   * @returns {string}
   */
  function formatReviewCount(count) {
    const parsedCount = Number(count);
    if (isNaN(parsedCount) || parsedCount <= 0) {
      return Number(DEFAULT_REVIEW_COUNT).toLocaleString('en-IN');
    }
    return parsedCount.toLocaleString('en-IN');
  }

  /**
   * Formats numeric rating score to one decimal place.
   * @param {number|string} score 
   * @returns {string}
   */
  function formatRatingScore(score) {
    const parsedScore = Number(score);
    if (isNaN(parsedScore) || parsedScore <= 0) {
      return Number(DEFAULT_RATING_SCORE).toFixed(1);
    }
    return parsedScore.toFixed(1);
  }

  /**
   * Reads and validates cached review ratings from localStorage.
   * @param {string} storageKey 
   * @param {number} ttlMs 
   * @returns {{ ratingScore: number, totalReviews: number }|null}
   */
  function getCachedRatings(storageKey, ttlMs) {
    try {
      const serializedData = localStorage.getItem(storageKey);
      if (!serializedData) {
        return null;
      }

      const parsedPayload = JSON.parse(serializedData);
      const hasValidTimestamp = parsedPayload && typeof parsedPayload.timestamp === 'number';
      const isCacheFresh = hasValidTimestamp && (Date.now() - parsedPayload.timestamp < ttlMs);

      if (isCacheFresh && parsedPayload.ratingScore && parsedPayload.totalReviews) {
        return {
          ratingScore: Number(parsedPayload.ratingScore),
          totalReviews: Number(parsedPayload.totalReviews)
        };
      }
    } catch (cacheError) {
      console.warn('[RepusoSync] Cache retrieval error:', cacheError);
    }
    return null;
  }

  /**
   * Writes validated review ratings to localStorage.
   * @param {string} storageKey 
   * @param {{ ratingScore: number, totalReviews: number }} ratingData 
   */
  function setCachedRatings(storageKey, ratingData) {
    try {
      if (!ratingData || !ratingData.totalReviews) {
        return;
      }
      const payloadToCache = {
        ratingScore: ratingData.ratingScore,
        totalReviews: ratingData.totalReviews,
        timestamp: Date.now()
      };
      localStorage.setItem(storageKey, JSON.stringify(payloadToCache));
    } catch (storageError) {
      console.warn('[RepusoSync] Cache write error:', storageError);
    }
  }

  /**
   * Fetches latest aggregated ratings from the Repuso API.
   * @param {string} endpoint 
   * @param {string} token 
   * @returns {Promise<{ ratingScore: number, totalReviews: number }>}
   */
  async function fetchRepusoRatings(endpoint, token) {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Repuso API response returned status ' + response.status);
    }

    const jsonResponse = await response.json();
    const hasTotals = jsonResponse && jsonResponse.totals;

    if (!hasTotals) {
      throw new Error('Invalid Repuso API payload structure');
    }

    return {
      ratingScore: Number(jsonResponse.totals.rating_value) || DEFAULT_RATING_SCORE,
      totalReviews: Number(jsonResponse.totals.official_num_reviews) || DEFAULT_REVIEW_COUNT
    };
  }

  /* ==========================================================================
     DOM Query & Update Logic (Data-Attribute Based)
     ========================================================================== */

  /**
   * Centralized DOM queries for review score elements.
   * @returns {NodeListOf<HTMLElement>}
   */
  function getReviewScoreElements() {
    return document.querySelectorAll('[data-repuso-review-score]');
  }

  /**
   * Centralized DOM queries for review count elements.
   * @returns {NodeListOf<HTMLElement>}
   */
  function getReviewCountElements() {
    return document.querySelectorAll('[data-repuso-review-count]');
  }

  /**
   * Centralized query for third-party reviews carousel container.
   * @returns {HTMLElement|null}
   */
  function getCarouselContainer() {
    return document.querySelector('[data-repuso-carousel]') || document.querySelector('#reviewsio-carousel-widget');
  }

  /**
   * Updates all score elements in the DOM.
   * @param {NodeListOf<HTMLElement>} scoreElements 
   * @param {string} formattedScore 
   */
  function updateScoreDisplay(scoreElements, formattedScore) {
    if (!scoreElements || scoreElements.length === 0) {
      return;
    }

    scoreElements.forEach(function (element) {
      try {
        if (!element) return;
        const currentText = (element.textContent || '').trim();
        if (element.classList.contains('rating') || element.closest('.trust-header, .trust-badge-section') || currentText.includes('/')) {
          element.textContent = formattedScore + ' / 5';
        } else if (element.textContent !== formattedScore) {
          element.textContent = formattedScore;
        }
      } catch (domError) {
        console.warn('[RepusoSync] Score element update error:', domError);
      }
    });
  }

  /**
   * Updates all count elements in the DOM.
   * @param {NodeListOf<HTMLElement>} countElements
   * @param {string} formattedCount
   * @param {string} [unfilteredCount] Override used only for the .main_subtite "Unfiltered" text.
   */
  function updateCountDisplay(countElements, formattedCount, unfilteredCount) {
    if (!countElements || countElements.length === 0) {
      return;
    }

    countElements.forEach(function (element) {
      try {
        if (!element) return;
        const currentText = (element.textContent || '').trim();

        // 1. Context check by class or DOM hierarchy for strict uniform formatting
        const isTrustHeaderReviews = element.classList.contains('reviews') || !!element.closest('.trust-header, .trust-badge-section');
        const isAnnouncementReviewText = element.classList.contains('annoucement-reviewtext');
        const isRatingText = element.classList.contains('rating-text');
        const isMainSubtitle = element.classList.contains('main_subtite');
        const isAnnouncementBarText = element.classList.contains('announcement-bar__text');

        if (isTrustHeaderReviews) {
          element.textContent = formattedCount + ' reviews on';
          return;
        }

        if (isAnnouncementReviewText) {
          element.textContent = formattedCount + ' Reviews';
          return;
        }

        if (isRatingText) {
          element.textContent = formattedCount + '+ Verified Reviews';
          return;
        }

        if (isMainSubtitle) {
          element.textContent = (unfilteredCount || formattedCount) + '+ reviews. Unfiltered. Since 2012';
          return;
        }

        if (isAnnouncementBarText) {
          element.textContent = 'from ' + formattedCount + ' verified reviews';
          return;
        }

        // 2. Fallback for empty element content
        if (currentText === '') {
          element.textContent = formattedCount + ' Reviews';
          return;
        }

        // 3. Dynamic text parsing for custom/other elements
        const hasReviewsWord = /reviews/i.test(currentText);
        const hasFromWord = /from/i.test(currentText);
        const hasOnWord = /on\s*$/i.test(currentText);
        const hasUnfilteredWord = /unfiltered/i.test(currentText);
        const hasReviewedByWord = /reviewed by/i.test(currentText);
        const hasVerifiedReviews = /verified reviews/i.test(currentText);

        if (hasUnfilteredWord) {
          element.textContent = currentText.replace(/[\d,.]+\+?\s+reviews/i, formattedCount + '+ reviews');
        } else if (hasReviewedByWord) {
          element.innerHTML = element.innerHTML.replace(/[\d,.]+\s+individual customers/i, formattedCount + ' individual customers');
        } else if (hasVerifiedReviews) {
          element.innerHTML = element.innerHTML.replace(/[\d,.]+\s*k?\s*\+?\s+verified reviews/i, formattedCount + '+ Verified Reviews');
        } else if (hasOnWord && hasReviewsWord) {
          element.textContent = formattedCount + ' reviews on';
        } else if (hasFromWord && hasReviewsWord) {
          element.textContent = currentText.replace(/[\d,.]+\s+reviews/i, formattedCount + ' reviews');
        } else if (hasReviewsWord) {
          element.textContent = formattedCount + ' Reviews';
        } else {
          element.textContent = formattedCount;
        }
      } catch (domError) {
        console.warn('[RepusoSync] Count element update error:', domError);
      }
    });
  }

  /**
   * Synchronizes reviews in the third-party carousel header without recursive loops.
   * @param {HTMLElement} carouselContainer 
   * @param {string} formattedScore 
   * @param {string} formattedCount 
   */
  function updateCarouselDisplay(carouselContainer, formattedScore, formattedCount) {
    if (!carouselContainer) {
      return;
    }

    try {
      const headerElements = carouselContainer.querySelectorAll('.CarouselWidget, .CarouselWidget-header, .CarouselWidget__header, h3, span, strong, b');
      headerElements.forEach(function (element) {
        if (element.children.length === 0 && element.textContent) {
          const isInsideItemList = element.closest('.R-ReviewsList, .R-ReviewsList__item, .CarouselWidget__item');
          if (isInsideItemList) {
            return;
          }

          const textValue = element.textContent.trim();

          if (/based on/i.test(textValue) && /reviews/i.test(textValue)) {
            if (!textValue.includes(formattedCount)) {
              element.textContent = textValue.replace(/based on\s+[\d,.]+\s+reviews/i, 'based on ' + formattedCount + ' reviews');
            }
          } else if (/^[\d,]{4,}$/.test(textValue) && textValue !== formattedCount) {
            element.textContent = formattedCount;
          } else if (/^4\.\d{1,2}$/.test(textValue) && textValue !== formattedScore) {
            element.textContent = formattedScore;
          }
        }
      });
    } catch (carouselError) {
      console.warn('[RepusoSync] Carousel update error:', carouselError);
    }
  }

  let isApplyingDomUpdates = false;

  /**
   * Applies the retrieved review data across all registered DOM components.
   * @param {{ ratingScore: number, totalReviews: number, isLive?: boolean }} ratingData
   * @param {boolean} [canUpdatePdp=true]
   */
  function handleApplyRatingsToDom(ratingData, canUpdatePdp) {
    if (!ratingData || isApplyingDomUpdates) {
      return;
    }

    const shouldUpdatePdp = typeof canUpdatePdp === 'boolean' ? canUpdatePdp : true;

    isApplyingDomUpdates = true;
    try {
      const formattedCount = formatReviewCount(ratingData.totalReviews);
      const formattedScore = formatRatingScore(ratingData.ratingScore);
      // The "X+ reviews. Unfiltered. Since 2012" subheading always shows a fixed
      // 30,000 when we don't have confirmed live API data (never the generic default).
      const unfilteredCount = ratingData.isLive
        ? formattedCount
        : formatReviewCount(UNFILTERED_REVIEW_COUNT_FALLBACK);

      const scoreElements = getReviewScoreElements();
      const countElements = getReviewCountElements();
      const carouselContainer = getCarouselContainer();

      updateScoreDisplay(scoreElements, formattedScore);
      updateCountDisplay(countElements, formattedCount, unfilteredCount);

      if (carouselContainer) {
        updateCarouselDisplay(carouselContainer, formattedScore, formattedCount);
      }

      if (shouldUpdatePdp) {
        updatePdpRatingBarDisplay(formattedScore, formattedCount);
      }
      updateReviewsPageDisplay(formattedScore, formattedCount);
    } finally {
      isApplyingDomUpdates = false;
    }
  }

  /**
   * Synchronizes review counters on the dedicated /pages/reviews widget.
   * @param {string} formattedScore
   * @param {string} formattedCount
   */
  function updateReviewsPageDisplay(formattedScore, formattedCount) {
    try {
      // 0. Direct target for Repuso Web Component elements
      const directOverallNums = document.querySelectorAll('rw-overall-rating .overall-num, .overall-num.sc-rw-overall-rating, .overall-num, rw-overall-rating span');
      directOverallNums.forEach(function (el) {
        if (el && /verified reviews/i.test(el.textContent) && !el.textContent.includes(formattedCount)) {
          el.textContent = 'from ' + formattedCount + ' verified reviews';
        }
      });

      function processSubtree(rootNode) {
        if (!rootNode) return;

        // 1. Text walker for direct text nodes
        try {
          const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_TEXT, null, false);
          let node;
          while ((node = walker.nextNode())) {
            const val = node.nodeValue;
            if (/[\d,]{4,}\s+verified reviews/i.test(val)) {
              node.nodeValue = val.replace(/[\d,]{4,}\s+verified reviews/i, formattedCount + ' verified reviews');
            } else if (/from\s+[\d,.]+/i.test(val) && /verified reviews/i.test(node.parentElement ? node.parentElement.textContent : '')) {
              node.nodeValue = val.replace(/from\s+[\d,.]+/i, 'from ' + formattedCount);
            } else if (/^[\d,]{4,}$/.test(val.trim()) && /verified reviews/i.test(node.parentElement ? node.parentElement.textContent : '')) {
              node.nodeValue = formattedCount;
            }
          }
        } catch (walkErr) { }

        // 2. Element search & shadow DOM traversal
        if (rootNode.querySelectorAll) {
          const allElements = rootNode.querySelectorAll('*');
          allElements.forEach(function (el) {
            if (el.shadowRoot) {
              processSubtree(el.shadowRoot);
            }

            if (el.children.length === 0 && el.textContent) {
              const text = el.textContent.trim();
              if (/[\d,]{4,}\s+verified reviews/i.test(text)) {
                el.textContent = text.replace(/[\d,]{4,}\s+verified reviews/i, formattedCount + ' verified reviews');
              } else if (/^25[,.]?481$/.test(text)) {
                el.textContent = formattedCount;
              }
            }
          });
        }
      }

      // Process main document
      processSubtree(document.body);

      // Process iframes
      const iframes = document.querySelectorAll('iframe');
      iframes.forEach(function (iframe) {
        try {
          const iframeDoc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
          if (iframeDoc) {
            processSubtree(iframeDoc);
          }
        } catch (iframeErr) { }
      });
    } catch (pageError) {
      console.warn('[RepusoSync] Reviews Page update error:', pageError);
    }
  }

  /**
   * Synchronizes the PDP top rating bar widget (Reviews.io rating bar).
   *
   * Strategy:
   *  - If [data-repuso-pdp-container] is empty (no inner HTML yet), build and
   *    inject the full star + text widget so the section only appears once real
   *    data is available.
   *  - If the widget was already injected, update the numbers in-place.
   *
   * @param {string} formattedScore
   * @param {string} formattedCount
   */
  function updatePdpRatingBarDisplay(formattedScore, formattedCount) {
    try {
      var ratingWrapper = document.querySelector('[data-repuso-pdp-container]');

      if (ratingWrapper) {
        // --- Container-based approach: inject HTML if empty or widget missing ---
        if (!ratingWrapper.querySelector('.RatingBarWidget')) {
          var scoreNum = parseFloat(formattedScore) || 4.7;
          var filledStars = Math.floor(scoreNum);
          var partial = Math.round((scoreNum - filledStars) * 10) * 10; // e.g. 70
          var gradId = 'pdp-star-grad-' + partial;

          var starsHtml = '';
          for (var i = 0; i < filledStars; i++) {
            starsHtml += '<svg width="17" height="17" viewBox="0 0 24 24" fill="#eec607"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>';
          }
          if (partial > 0 && filledStars < 5) {
            starsHtml += '<svg width="17" height="17" viewBox="0 0 24 24" fill="url(#' + gradId + ')">'
              + '<defs><linearGradient id="' + gradId + '" x1="0%" y1="0%" x2="100%" y2="0%">'
              + '<stop offset="' + partial + '%" stop-color="#eec607"/>'
              + '<stop offset="' + partial + '%" stop-color="#cccccc"/>'
              + '</linearGradient></defs>'
              + '<path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>'
              + '</svg>';
          }

          ratingWrapper.innerHTML =
            '<div class="RatingBarWidget RatingBarWidget-prefix">'
            + '<div class="RatingBarWidget__inner">'
            + '<div class="R-flex-row R-flex-center-xxs" style="display:flex;align-items:center;gap:6px;font-size:14px;color:#2b2b2b;">'
            + '<div class="R-RatingStars R-RatingStars--sm">'
            + '<div class="R-RatingStars__stars" title="' + formattedScore + ' Stars" style="display:flex;align-items:center;gap:2px;">'
            + starsHtml
            + '</div></div>'
            + '<div class="R-TextBody R-TextBody--xxs R-TextBody--inline is-synced">'
            + '<strong class="cssVar-reviewStatistics" data-repuso-review-score>' + formattedScore + '</strong>'
            + '<span class="cssVar-reviewText" style="display:none;">Based on </span>'
            + '<span class="cssVar-reviewStatistics" data-repuso-review-count>' + formattedCount + '</span>'
            + '<span class="cssVar-reviewText"> Store Reviews</span>'
            + '</div>'
            + '</div></div></div>';
        } else {
          // Already rendered — just update the numbers
          var scoreEl = ratingWrapper.querySelector('[data-repuso-review-score]');
          var countEl = ratingWrapper.querySelector('[data-repuso-review-count]');
          if (scoreEl && scoreEl.textContent.trim() !== formattedScore) {
            scoreEl.textContent = formattedScore;
          }
          if (countEl && countEl.textContent.trim() !== formattedCount) {
            countEl.textContent = formattedCount;
          }
        }
        return;
      }

      // --- Legacy fallback: update existing hardcoded widget elements ---
      var legacyWrapper = document.querySelector('.rating-wrapper');
      if (!legacyWrapper) return;

      var statElements = legacyWrapper.querySelectorAll('.cssVar-reviewStatistics');
      if (statElements.length > 0) {
        if (statElements[0]) {
          if (!statElements[0].hasAttribute('data-repuso-review-score')) {
            statElements[0].setAttribute('data-repuso-review-score', '');
          }
          if (statElements[0].textContent.trim() !== formattedScore) {
            statElements[0].textContent = formattedScore;
          }
        }
        if (statElements.length >= 2 && statElements[1]) {
          if (!statElements[1].hasAttribute('data-repuso-review-count')) {
            statElements[1].setAttribute('data-repuso-review-count', '');
          }
          if (statElements[1].textContent.trim() !== formattedCount) {
            statElements[1].textContent = formattedCount;
          }
        }
      }

      var ratingStars = legacyWrapper.querySelector('.R-RatingStars__stars');
      if (ratingStars && ratingStars.getAttribute('title') !== formattedScore + ' Stars') {
        ratingStars.setAttribute('title', formattedScore + ' Stars');
      }

      var reviewTextEl = legacyWrapper.querySelector('.cssVar-reviewText:last-child');
      if (reviewTextEl && !reviewTextEl.textContent.includes('Reviews')) {
        reviewTextEl.textContent = ' Store Reviews';
      }

      var ratingBarInner = legacyWrapper.querySelector('.R-TextBody');
      if (ratingBarInner && !ratingBarInner.classList.contains('is-synced')) {
        ratingBarInner.classList.add('is-synced');
      }
    } catch (ratingBarError) {
      console.warn('[RepusoSync] PDP Rating Bar update error:', ratingBarError);
    }
  }

  /**
   * Applies rating data specifically to cart announcement bar elements.
   * @param {{ ratingScore: number, totalReviews: number }} ratingData
   */
  function updateCartBarDisplay(ratingData) {
    if (!ratingData) return;
    try {
      const formattedScore = formatRatingScore(ratingData.ratingScore);
      const formattedCount = formatReviewCount(ratingData.totalReviews);

      const scoreEl = document.querySelector('.annoucement-reviewnum[data-repuso-review-score]');
      if (scoreEl && scoreEl.textContent.trim() !== formattedScore) {
        scoreEl.textContent = formattedScore;
      }

      const countEl = document.querySelector('.annoucement-reviewtext[data-repuso-review-count]');
      if (countEl && countEl.textContent.trim() !== formattedCount + ' Reviews') {
        countEl.textContent = formattedCount + ' Reviews';
      }
    } catch (cartBarErr) {
      console.warn('[RepusoSync] Cart bar update error:', cartBarErr);
    }
  }

  /**
   * Renders the PDP shimmer skeleton loader placeholder if the rating bar is not yet populated.
   * @param {Element} container
   */
  function renderPdpSkeletonLoader(container) {
    try {
      if (!container) return;
      if (container.querySelector('.RatingBarWidget') || container.querySelector('[data-repuso-pdp-loader]')) {
        return;
      }
      container.innerHTML =
        '<div class="repuso-skeleton-loader" data-repuso-pdp-loader aria-hidden="true">'
        + '<div class="repuso-skeleton-stars">'
        + '<span class="repuso-skeleton-star"><svg width="17" height="17" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg></span>'
        + '<span class="repuso-skeleton-star"><svg width="17" height="17" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg></span>'
        + '<span class="repuso-skeleton-star"><svg width="17" height="17" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg></span>'
        + '<span class="repuso-skeleton-star"><svg width="17" height="17" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg></span>'
        + '<span class="repuso-skeleton-star"><svg width="17" height="17" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg></span>'
        + '</div>'
        + '<span class="repuso-skeleton-text"></span>'
        + '</div>';
    } catch (loaderError) {
      console.warn('[RepusoSync] Skeleton loader render error:', loaderError);
    }
  }

  /* ==========================================================================
     Orchestration & Initialization
     ========================================================================== */

  /**
   * Initializes Repuso ratings synchronization with real live API data.
   */
  async function initRepusoReviewsSync() {
    let currentRatingData = getCachedRatings(STORAGE_CACHE_KEY, CACHE_TTL_MS);
    let isPdpApiDataReady = false;

    // Step 1: Render / ensure PDP skeleton loader on the PDP rating container immediately
    const pdpRatingWrapper = document.querySelector('[data-repuso-pdp-container]') || document.querySelector('.rating-wrapper');
    if (pdpRatingWrapper && !pdpRatingWrapper.querySelector('.RatingBarWidget')) {
      renderPdpSkeletonLoader(pdpRatingWrapper);
    }

    if (pdpRatingWrapper && window.MutationObserver) {
      try {
        const pdpObserver = new MutationObserver(function () {
          if (isPdpApiDataReady && currentRatingData) {
            updatePdpRatingBarDisplay(
              formatRatingScore(currentRatingData.ratingScore),
              formatReviewCount(currentRatingData.totalReviews)
            );
          }
        });
        pdpObserver.observe(pdpRatingWrapper, { childList: true, subtree: true, characterData: true });
      } catch (pdpObsErr) { }
    }

    // Step 2: Apply rating data immediately to DOM for OTHER sections ONLY (cart drawer, carousel, reviews page).
    // The PDP rating bar remains in shimmer skeleton loading state until live API data arrives.
    const initialRatingData = currentRatingData || { ratingScore: DEFAULT_RATING_SCORE, totalReviews: DEFAULT_REVIEW_COUNT, isLive: false };
    handleApplyRatingsToDom(initialRatingData, false);
    updateCartBarDisplay(initialRatingData);

    // Step 3: Fetch 100% live, real-time data from Repuso API in background
    try {
      const freshData = await fetchRepusoRatings(API_ENDPOINT, API_TOKEN);
      if (freshData && freshData.totalReviews) {
        freshData.isLive = true;
        currentRatingData = freshData;
        setCachedRatings(STORAGE_CACHE_KEY, freshData);
      } else {
        console.warn('[RepusoSync] Live API fetch returned no usable data, keeping fallback:', freshData);
      }
    } catch (fetchError) {
      console.warn('[RepusoSync] Live API synchronization error, applying fallback values:', fetchError);
      if (!currentRatingData) {
        currentRatingData = { ratingScore: DEFAULT_RATING_SCORE, totalReviews: DEFAULT_REVIEW_COUNT, isLive: false };
      }
    } finally {
      // API call completed (live data or safe fallback) -> transition PDP to live rating bar
      isPdpApiDataReady = true;
      if (currentRatingData) {
        handleApplyRatingsToDom(currentRatingData, true);
        updateCartBarDisplay(currentRatingData);
      }
    }

    // Step 4: Guarded watcher for late-loading dynamic widgets
    let pollCount = 0;
    const pollTimer = setInterval(function () {
      pollCount += 1;
      if (currentRatingData) {
        handleApplyRatingsToDom(currentRatingData, isPdpApiDataReady);
        updateCartBarDisplay(currentRatingData);
      }

      if (pollCount >= 40) {
        clearInterval(pollTimer);
      }
    }, 150);

    // Step 5: Persistent cart drawer observer — re-applies data whenever cart opens
    if (window.MutationObserver) {
      try {
        const cartDrawerObserver = new MutationObserver(function (mutations) {
          if (!currentRatingData) return;
          for (var i = 0; i < mutations.length; i++) {
            var mutation = mutations[i];
            if (mutation.type === 'attributes' || mutation.type === 'childList') {
              updateCartBarDisplay(currentRatingData);
              break;
            }
          }
        });

        // Observe the cart drawer element for visibility, class changes, and DOM replacements (AJAX cart updates)
        var cartDrawerEl = document.querySelector('cart-drawer, #CartDrawer, .cart-drawer');
        if (cartDrawerEl) {
          cartDrawerObserver.observe(cartDrawerEl, {
            attributes: true,
            attributeFilter: ['class', 'open', 'aria-expanded'],
            childList: true,
            subtree: true
          });
        }

        // Also listen to cart update events
        document.addEventListener('cart:updated', function () {
          if (currentRatingData) updateCartBarDisplay(currentRatingData);
        });
        document.addEventListener('cart:update', function () {
          if (currentRatingData) updateCartBarDisplay(currentRatingData);
        });
      } catch (cartObsErr) { }
    }

    // Step 6: Dedicated observer for /pages/reviews Stencil component
    if (window.MutationObserver && (window.location.pathname.indexOf('reviews') !== -1 || document.querySelector('div[data-repuso-grid], div[data-rw-grid], rw-widget-grid'))) {
      try {
        const reviewsPageObserver = new MutationObserver(function () {
          const overallNumEl = document.querySelector('span.overall-num, .overall-num');
          if (overallNumEl && !overallNumEl.textContent.includes(formatReviewCount(currentRatingData.totalReviews))) {
            overallNumEl.textContent = 'from ' + formatReviewCount(currentRatingData.totalReviews) + ' verified reviews';
          }
        });
        reviewsPageObserver.observe(document.body, { childList: true, subtree: true });
      } catch (obsErr) { }
    }
  }

  // Self-initialize on DOM readiness
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRepusoReviewsSync);
  } else {
    initRepusoReviewsSync();
  }
})();
